import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';

export default function ThankYou() {
  useLanguageFromPath();
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);

  const steps = [
    {
      num: '1',
      icon: 'ri-search-eye-line',
      title: t('ty_step_1_title'),
      desc: t('ty_step_1_desc'),
      time: t('ty_step_1_time'),
    },
    {
      num: '2',
      icon: 'ri-phone-line',
      title: t('ty_step_2_title'),
      desc: t('ty_step_2_desc'),
      time: t('ty_step_2_time'),
    },
    {
      num: '3',
      icon: 'ri-route-line',
      title: t('ty_step_3_title'),
      desc: t('ty_step_3_desc'),
      time: t('ty_step_3_time'),
    },
    {
      num: '4',
      icon: 'ri-guide-line',
      title: t('ty_step_4_title'),
      desc: t('ty_step_4_desc'),
      time: t('ty_step_4_time'),
    },
  ];

  const waitLinks = [
    { title: t('ty_wait_link_1'), icon: 'ri-file-list-3-line', slug: lp('/blog', lang) + '/funding-readiness-checklist' },
    { title: t('ty_wait_link_2'), icon: 'ri-route-line', slug: lp('/how-it-works', lang) },
    { title: t('ty_wait_link_3'), icon: 'ri-scales-3-line', slug: lp('/blog', lang) + '/plan-1-vs-plan-2-which-is-right-for-your-business' },
    { title: t('ty_wait_link_4'), icon: 'ri-error-warning-line', slug: lp('/blog', lang) + '/7-mistakes-that-hurt-business-funding-applications' },
  ];

  const trustBadges = [
    { icon: 'ri-lock-2-line', text: t('ty_trust_1') },
    { icon: 'ri-shield-check-line', text: t('ty_trust_2') },
    { icon: 'ri-customer-service-2-line', text: t('ty_trust_3') },
  ];

  useEffect(() => {
    document.title = 'Application Received | Kailac — What Happens Next';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Your Kailac application has been received. A real advisor will review your profile within 48 hours and reach out to discuss your funding options. Subject to eligibility.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />
      <main className="flex flex-col items-center justify-center min-h-screen px-6 text-center py-24">
        {/* Success Icon */}
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#F8F3FB] mb-8">
          <i className="ri-check-double-line text-[#0F766E] text-3xl"></i>
        </div>

        {/* Main Message */}
        <div className="max-w-2xl">
          <p className="text-xs font-semibold text-[#0F766E] uppercase tracking-widest mb-3">{t('ty_badge')}</p>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">
            {t('ty_title')}
          </h1>
          <p className="text-lg text-gray-500 mb-3 leading-relaxed">
            {t('ty_subtitle')} <strong className="text-gray-800">{t('ty_subtitle_hours')}</strong>.
          </p>
          <p className="text-sm text-gray-400 italic mb-10">
            {t('ty_disclaimer')}
          </p>
        </div>

        {/* What Happens Next */}
        <div className="bg-[#F8F3FB] rounded-2xl p-8 max-w-2xl w-full mb-10 border border-[#E9D9F2]">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-left">{t('ty_next_title')}</h2>
          <div className="space-y-5 text-left">
            {steps.map(({ num, icon, title, desc, time }) => (
              <div key={num} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#4F2D63] text-white text-sm font-bold flex-shrink-0">
                  {num}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <span className="text-xs text-[#0F766E] font-medium whitespace-nowrap bg-[#F8F3FB] px-2 py-0.5 rounded-full">{time}</span>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed mt-1">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* While You Wait */}
        <div className="bg-[#F8F3FB] border border-[#E9D9F2] rounded-2xl p-8 max-w-2xl w-full mb-10">
          <h2 className="text-lg font-bold text-gray-900 mb-2">{t('ty_wait_title')}</h2>
          <p className="text-sm text-gray-500 mb-6">{t('ty_wait_subtitle')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {waitLinks.map(({ title, icon, slug }) => (
              <Link
                key={slug}
                to={slug}
                className="flex items-center gap-3 bg-white border border-[#E9D9F2] rounded-xl px-4 py-3 text-sm text-gray-700 hover:border-[#4F2D63] hover:text-[#4F2D63] transition-colors cursor-pointer"
              >
                <i className={`${icon} text-[#4F2D63]`}></i>
                {title}
              </Link>
            ))}
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-6 mb-10 text-sm text-gray-400">
          {trustBadges.map(({ icon, text }) => (
            <div key={text} className="flex items-center gap-2">
              <i className={`${icon} text-[#0F766E]`}></i>
              <span>{text}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-4">
          <Link
            to={lp('/', lang)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            {t('ty_btn_home')}
          </Link>
          <Link
            to={lp('/blog', lang)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#4F2D63] text-white font-semibold text-sm hover:bg-[#3E234F] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-book-open-line"></i>
            {t('ty_btn_resources')}
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
